import React, { useState } from "react";
import { Upload, FileImage, Download, X, Loader2 } from "lucide-react";
import { Modal, Button, Spinner } from "../lib/ui";
import { compressImage } from "../lib/image";
import { useT } from "../lib/i18n";
import { useToast } from "../lib/toast";
import api from "../lib/api";

export const rp = (v) => (v == null ? "Rp 0" : "Rp " + Math.round(Number(v)).toLocaleString("id-ID"));

// Invoice photo picker -> compresses to base64 data URL via onChange(dataUrl)
export function InvoiceField({ value, onChange, testid = "invoice-field" }) {
  const { t } = useT();
  const toast = useToast();
  const [busy, setBusy] = useState(false);

  const pick = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBusy(true);
    try {
      const dataUrl = await compressImage(file, 1280, 0.8);
      onChange(dataUrl);
    } catch {
      toast.error(t("Could not read image"));
    } finally {
      setBusy(false);
      e.target.value = "";
    }
  };

  return (
    <div data-testid={testid}>
      <label className="mb-1 block text-sm font-medium text-ink-soft">{t("Invoice Photo")}</label>
      {value ? (
        <div className="flex items-center gap-3">
          <img src={value} alt="invoice" className="h-16 w-16 rounded-lg border border-line object-cover" />
          <Button type="button" variant="ghost" size="sm" onClick={() => onChange(null)} data-testid={`${testid}-remove`}>
            <X size={14} /> {t("Remove")}
          </Button>
        </div>
      ) : (
        <label className="flex cursor-pointer items-center gap-2 rounded-xl border border-dashed border-line bg-stone-50 px-4 py-3 text-sm text-ink-soft hover:border-primary hover:text-primary">
          {busy ? <Loader2 size={16} className="animate-spin" /> : <Upload size={16} />}
          {busy ? t("Compressing…") : t("Upload invoice photo")}
          <input type="file" accept="image/*" className="hidden" onChange={pick} disabled={busy} data-testid={`${testid}-input`} />
        </label>
      )}
    </div>
  );
}

// View + download an archived invoice image by purchase id
export function InvoiceViewerModal({ purchaseId, open, onClose }) {
  const { t } = useT();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  React.useEffect(() => {
    if (!open || !purchaseId) { setData(null); return; }
    setLoading(true);
    api.get(`/purchasing/${purchaseId}/invoice`)
      .then(({ data }) => setData(data))
      .catch(() => setData({ error: true }))
      .finally(() => setLoading(false));
  }, [open, purchaseId]);

  return (
    <Modal open={open} onClose={onClose} title={t("Invoice")} size="lg"
      footer={
        data?.invoice_image ? (
          <a href={data.invoice_image} download={`faktur-${data.reference_no || purchaseId}.jpg`} data-testid="invoice-download">
            <Button variant="outline"><Download size={16} /> {t("Download")}</Button>
          </a>
        ) : <Button variant="outline" onClick={onClose}>{t("Close")}</Button>
      }>
      {loading ? <div className="flex h-48 items-center justify-center"><Spinner className="h-7 w-7" /></div>
        : data?.invoice_image ? (
          <div className="text-center" data-testid="invoice-image-wrap">
            <p className="mb-2 text-xs text-ink-mute">{data.reference_no} · {data.invoice_no || t("No invoice number")}</p>
            <img src={data.invoice_image} alt="invoice" className="mx-auto max-h-[60vh] rounded-lg border border-line" />
          </div>
        ) : (
          <div className="flex h-48 flex-col items-center justify-center text-ink-mute">
            <FileImage size={36} className="mb-2 opacity-50" />
            <p className="text-sm">{t("No invoice image attached")}</p>
          </div>
        )}
    </Modal>
  );
}
